import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CUSTOMERS } from '../lib/customers';
import type { Customer } from '../lib/api';
import { createOnePager, listDocs, openDoc, type Session, type DocSummary } from '../lib/collateral';
import CustomerRail from '../components/CustomerRail';
import OnePagerEditor from '../components/OnePagerEditor';

type Theme = 'light' | 'dark';
type Audience = 'client-facing' | 'internal';

interface Artifact {
  id: string;
  label: string;
  blurb: string;
  active: boolean;
}

const ARTIFACTS: Artifact[] = [
  { id: 'one-pager', label: 'One-pager', blurb: 'A single branded page.', active: true },
  { id: 'proposal', label: 'Proposal', blurb: 'Multi-page pitch document.', active: false },
  { id: 'deck', label: 'Deck', blurb: 'Slide-by-slide narrative.', active: false },
  { id: 'video', label: 'Product video', blurb: 'A short motion explainer.', active: false },
];

/** A sensible starting brief per target — real, outcome-led, never lorem. */
function defaultBrief(customer: Customer): string {
  if (customer.id === 'moxo') {
    return (
      'A one-page overview of Moxo for prospective customers: what it is, the core ' +
      'capabilities (branded client portals, workflow automation, e-sign, approvals, ' +
      'payments), and why teams switch. Outcome-led and confident, no jargon.'
    );
  }
  const industry = customer.industry ? customer.industry.toLowerCase() : 'client';
  const lead = customer.facts?.[0];
  const outcome = lead ? ` Lead with the headline result: ${lead.toLowerCase()}.` : '';
  return (
    `A client-facing one-pager showing how ${customer.brand.companyName} runs its ${industry} ` +
    'workflows on Moxo — branded onboarding, document collection, and approvals in one ' +
    `portal.${outcome}`
  );
}

export default function StudioPage() {
  const [theme, setTheme] = useState<Theme>(() =>
    (typeof localStorage !== 'undefined' && localStorage.getItem('studio-theme')) === 'dark' ? 'dark' : 'light',
  );
  const [selectedCustomer, setSelectedCustomer] = useState<Customer>(CUSTOMERS[0]);
  const [artifact, setArtifact] = useState<string>('one-pager');
  const [brief, setBrief] = useState<string>(() => defaultBrief(CUSTOMERS[0]));
  const [audience, setAudience] = useState<Audience>('client-facing');
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [docs, setDocs] = useState<DocSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('studio-theme', theme);
  }, [theme]);

  // Refresh the saved-one-pagers list whenever we're on the home (not editing),
  // so a newly-created or just-edited one appears.
  const refreshDocs = useCallback(() => { listDocs().then(setDocs).catch(() => {}); }, []);
  useEffect(() => { if (!session) refreshDocs(); }, [session, refreshDocs]);

  async function openSaved(name: string) {
    setError(null);
    try {
      setSession(await openDoc(name));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open that one-pager.');
    }
  }

  // Reopen a specific one-pager when arrived at via /?open=<name> (from Assets).
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const name = params.get('open');
    if (!name) return;
    setParams({}, { replace: true });
    openSaved(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    setBrief(defaultBrief(selectedCustomer));
  }, [selectedCustomer]);

  async function handleGenerate() {
    setLoading(true);
    setError(null);
    try {
      const s = await createOnePager(selectedCustomer, brief.trim(), audience);
      setSession(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const canGenerate = !loading && brief.trim().length > 0;

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100vh', minHeight: 0 }}>
      {/* The edit loop takes over the whole surface once a one-pager is authored. */}
      {session && (
        <OnePagerEditor session={session} onChange={setSession} onExit={() => setSession(null)} />
      )}

      {/* ── Top bar ── */}
      <header
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          height: 56,
          padding: '0 1.1rem',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <span className="font-display" style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>Studio</span>
        <span style={{ color: 'var(--border-strong)' }}>/</span>
        <span className="font-mono" style={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Collateral</span>
        <span style={{ flex: 1 }} />
        <Link to="/assets" className="btn" style={{ padding: '0.35rem 0.7rem', fontSize: 13, textDecoration: 'none' }}>Assets</Link>
        <button
          type="button"
          className="btn"
          onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
          aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
          style={{ padding: '0.35rem 0.7rem', fontSize: 13 }}
        >
          {theme === 'light' ? '☾ Dark' : '☀ Light'}
        </button>
      </header>

      {/* ── 3-column workspace ── */}
      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr) 340px' }}>
        {/* LEFT — target picker */}
        <aside style={{ borderRight: '1px solid var(--border)', padding: '1rem 0.8rem', overflow: 'auto', background: 'var(--surface)' }}>
          <div className="sec-label" style={{ marginBottom: '0.7rem', paddingLeft: '0.2rem' }}>Target</div>
          <CustomerRail customers={CUSTOMERS} selectedId={selectedCustomer.id} onSelect={setSelectedCustomer} />

          {docs.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <div className="sec-label" style={{ marginBottom: '0.6rem', paddingLeft: '0.2rem' }}>Saved one-pagers</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {docs.map((d) => (
                  <button
                    key={d.name}
                    type="button"
                    onClick={() => openSaved(d.name)}
                    className="card-2"
                    title={d.title}
                    style={{ textAlign: 'left', padding: '0.5rem 0.6rem', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</span>
                    <span className="font-mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>edit · v{d.headSeq}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* CENTER — artifact + brief */}
        <main style={{ overflow: 'auto', padding: '1.4rem 1.6rem' }}>
          <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
            <section>
              <div className="sec-label" style={{ marginBottom: '0.7rem' }}>Choose an artifact</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem' }}>
                {ARTIFACTS.map((a) => {
                  const activeSel = a.active && artifact === a.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      disabled={!a.active}
                      aria-pressed={activeSel}
                      onClick={() => a.active && setArtifact(a.id)}
                      className="card-2"
                      style={{
                        textAlign: 'left', padding: '0.8rem 0.85rem', display: 'flex', flexDirection: 'column', gap: '0.3rem', minHeight: 80,
                        cursor: a.active ? 'pointer' : 'not-allowed', opacity: a.active ? 1 : 0.6,
                        borderColor: activeSel ? 'var(--accent)' : 'var(--border)',
                        background: activeSel ? 'var(--accent-soft)' : 'var(--surface-2)',
                        color: activeSel ? 'var(--accent)' : 'var(--text)',
                        transition: 'border-color 0.12s ease, background 0.12s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                        <span className="font-display" style={{ fontWeight: 600, fontSize: 15 }}>{a.label}</span>
                        {!a.active && (
                          <span className="chip" style={{ background: 'var(--surface)', color: 'var(--text-dim)', border: '1px solid var(--border)' }}>soon</span>
                        )}
                      </div>
                      <span style={{ fontSize: 11.5, color: activeSel ? 'var(--accent)' : 'var(--text-dim)' }}>{a.blurb}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="brief" className="sec-label">Brief</label>
              <textarea
                id="brief" className="input" rows={4} value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="What should this one-pager communicate?"
                style={{ resize: 'vertical', lineHeight: 1.5 }}
              />
            </section>

            <section style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: 260 }}>
              <label htmlFor="audience" className="sec-label">Audience</label>
              <select id="audience" className="input" value={audience} onChange={(e) => setAudience(e.target.value as Audience)}>
                <option value="client-facing">Client-facing</option>
                <option value="internal">Internal</option>
              </select>
            </section>
          </div>
        </main>

        {/* RIGHT — generate */}
        <aside style={{ borderLeft: '1px solid var(--border)', padding: '1.2rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.7rem', minHeight: 0, background: 'var(--surface)' }}>
          <div className="sec-label">Output</div>
          <button
            type="button" className="btn-primary" disabled={!canGenerate} onClick={handleGenerate}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' }}
          >
            {loading ? (
              <>
                <span className="spin" style={{ borderColor: 'var(--accent-ink)', borderTopColor: 'transparent' }} />
                Authoring…
              </>
            ) : (
              <>Generate &amp; edit</>
            )}
          </button>

          {error && (
            <div role="alert" style={{ background: 'var(--danger-soft)', color: 'var(--danger)', border: '1px solid var(--danger)', borderRadius: 10, padding: '0.6rem 0.7rem', fontSize: 12.5, lineHeight: 1.4 }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 'auto', fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.5, borderTop: '1px solid var(--border)', paddingTop: '0.9rem' }}>
            Generate a branded one-pager, then <strong style={{ color: 'var(--text)' }}>click any element to edit it</strong> — tweak it directly or describe a change and the AI makes it. Every change previews before it saves, and you can step back through versions.
          </div>
        </aside>
      </div>
    </div>
  );
}
